'use client';

import { faUserGroup, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { Button, Checkbox, Typography, Tooltip } from 'src/components';
import { PageHeader } from 'src/components/Settings/components';
import { useGetAllTeams } from 'src/hooks/teams/useGetAllTeams';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Team } from 'src/api/redux/teams';
import { useNotification } from 'src/context/notification';
import useUpdateTeam from 'src/hooks/teams/useUpdateTeam';
import { useGetCompanyUserQuery } from 'src/api/redux';

const TeamAssignments = () => {
  const { companyId, userId } = useParams<{ companyId: string; userId: string }>();
  const { data: allTeams, isLoading } = useGetAllTeams();
  const { updateTeam } = useUpdateTeam();

  const { data: currentUser } = useGetCompanyUserQuery(
    { companyId, userId },
    { skip: !companyId || !userId }
  );
  const { setNotification } = useNotification();

  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [initialTeams, setInitialTeams] = useState<Team[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const userTeams = useMemo(() => {
    return (
      allTeams?.results?.filter((team) =>
        team.users?.some((user) => user.id === String(currentUser?.id))
      ) || []
    );
  }, [allTeams, currentUser]);

  useEffect(() => {
    if (userTeams?.length > 0) {
      setSelectedTeams(userTeams);
      setInitialTeams(userTeams);
    }
  }, [userTeams]);

  useEffect(() => {
    if (initialTeams.length > 0) {
      const initialTeamIds = initialTeams.map((team) => team.id);
      const selectedTeamIds = selectedTeams.map((team) => team.id);

      const hasChanges =
        initialTeamIds.length !== selectedTeamIds.length ||
        initialTeamIds.some((id) => !selectedTeamIds.includes(id)) ||
        selectedTeamIds.some((id) => !initialTeamIds.includes(id));

      setHasChanges(hasChanges);
    }
  }, [selectedTeams, initialTeams]);

  const handleTeamToggle = (team: Team) => {
    // Check if this is the full company team (name contains "company" case-insensitive)
    const isFullCompanyTeam = team.name.toLowerCase() === 'full company team';

    // Don't allow toggling if it's the full company team
    if (isFullCompanyTeam) {
      return;
    }

    setSelectedTeams((prev) => {
      if (prev.includes(team)) {
        return prev.filter((t) => t !== team);
      } else {
        return [...prev, team];
      }
    });
  };

  const handleCancel = () => {
    // Reset to initial state
    setSelectedTeams([...initialTeams]);
    setHasChanges(false);
  };

  const handleUpdate = async () => {
    try {
      const allTeamsList = allTeams?.results || [];

      const updatePromises = allTeamsList.map(async (team) => {
        const isSelected = selectedTeams.some(
          (selectedTeam) => selectedTeam.id === team.id
        );
        const isInitiallySelected = initialTeams.some(
          (initialTeam) => initialTeam.id === team.id
        );

        if (isSelected !== isInitiallySelected) {
          const currentUserIds = team.users.map((user) => user.id);

          // Add or remove the current user
          let updatedUserIds = [...currentUserIds];
          const currentUserIdStr = String(currentUser?.id);
          if (isSelected && !currentUserIds.includes(currentUserIdStr)) {
            // Add user to team
            updatedUserIds.push(currentUserIdStr);
          } else if (!isSelected && currentUserIds.includes(currentUserIdStr)) {
            // Remove user from team
            updatedUserIds = updatedUserIds.filter(
              (id) => id !== currentUserIdStr
            );
          }

          // Only update if there's a change
          if (
            JSON.stringify(currentUserIds) !== JSON.stringify(updatedUserIds)
          ) {
            return updateTeam(team.id, {
              id: team.id,
              name: team.name,
              description: team.description,
              users: updatedUserIds,
            });
          }
        }
        return null;
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises.filter(Boolean));

      // After successful update, set the new selection as the initial state
      setInitialTeams([...selectedTeams]);
      setHasChanges(false);

      // Show success notification
      setNotification({
        title: 'Success',
        message: 'Team assignments updated successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error updating teams:', error);
      // Show error notification
      setNotification({
        title: 'Error',
        message: 'Failed to update team assignments',
        type: 'error',
      });
    }
  };

  return (
    <div className="relative flex flex-col gap-6">
      <PageHeader className="bg-sky-50">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h1 className="text-xl font-semibold text-neutral-700">
              Team Assignments
            </h1>
            <Typography className="text-sm text-neutral">
              Select which teams this user should be a member of.
            </Typography>
          </div>
        </div>
      </PageHeader>
      {isLoading && <div>Loading...</div>}
      <div className="p-6">
        <div className="grid grid-cols-1 gap-4 mb-6">
          {allTeams?.results?.map((team) => {
            // Check if this is the full company team (name contains "company" case-insensitive)
            const isFullCompanyTeam = team.name
              .toLowerCase()
              .includes('company');

            return (
              <div
                key={team.id}
                className="flex items-start gap-4 p-6 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                {isFullCompanyTeam ? (
                  <Tooltip content="Active users cannot be removed from the full company team">
                    <div className="mt-1 w-5 h-5 flex items-center justify-center">
                      <Icon icon={faInfoCircle} className="text-neutral-500" />
                    </div>
                  </Tooltip>
                ) : (
                  <Checkbox
                    id={team.id}
                    data-testid={`team-checkbox-${team.id}`}
                    checked={selectedTeams.some(
                      (selectedTeam) => selectedTeam.id === team.id
                    )}
                    onChange={() => handleTeamToggle(team)}
                    className="mt-1"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Typography className="font-semibold text-gray-900 cursor-pointer">
                      {team.name}
                    </Typography>
                    <div className="flex items-center bg-neutral-100 rounded-full px-2 py-0.5">
                      <Typography className="text-xs text-neutral-600">
                        <Icon
                          icon={faUserGroup}
                          size="xs"
                          className="mr-1 text-neutral-600"
                        />
                        {team.users?.length}
                      </Typography>
                    </div>
                  </div>
                  <Typography className="text-sm text-gray-600 leading-relaxed">
                    {team.description}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outlined"
            color="neutral"
            disabled={!hasChanges}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            color="neutral"
            variant="solid"
            disabled={!hasChanges}
            onClick={handleUpdate}
          >
            Update
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamAssignments;
